import { HttpInterceptorFn, HttpResponse } from '@angular/common/http';
import { Observable, of } from 'rxjs';
import { mockDb } from '../mocks/mock-db';
import { SEED_CREDENTIALS } from '../../../seeder/index';
import { environment } from '../../environments/environment';

const API_BASE = environment.API_URL;

// ── Helpers ─────────────────────────────────────────────────────────────────

function ok(body: unknown = null): Observable<HttpResponse<unknown>> {
  return of(new HttpResponse<unknown>({ status: 200, body }));
}

function notFound(): Observable<HttpResponse<unknown>> {
  return of(new HttpResponse<unknown>({ status: 404, body: { message: 'Not found' } }));
}

function wrap(data: unknown): object {
  return { data, message: 'OK', status: 200 };
}

function mutationOk(message: string, data?: unknown): object {
  return data !== undefined ? { message, data, status: 200 } : { message, status: 200 };
}

/** Creates a minimal JWT that atob() in StorageService.decodeJWT() can parse. */
function makeMockJWT(payload: object): string {
  const enc = (obj: object) => btoa(JSON.stringify(obj));
  return `${enc({ alg: 'HS256', typ: 'JWT' })}.${enc(payload)}.mock_sig`;
}

/** Strip query-string and return just the path segment after the API base. */
function path(url: string): string {
  return url.replace(API_BASE, '').split('?')[0];
}

function num(s: string): number { return parseInt(s, 10); }

function assignmentName(field: string, value: number | null | undefined): string | null {
  if (!value) return null;
  if (field === 'project') return mockDb.projects.find(item => item.id === value)?.name ?? String(value);
  if (field === 'zone') return mockDb.zones.find(item => item.id === value)?.name ?? String(value);
  if (field === 'primary_worker') {
    const worker = mockDb.workers.find(item => item.id === value);
    return worker ? `${worker.first_name} ${worker.last_name}` : String(value);
  }
  return String(value);
}

function recordDeviceActivity(deviceId: number, field: string, oldValue: string | null, newValue: string | null): void {
  if (oldValue === newValue) return;
  mockDb.add(mockDb.deviceActivities, {
    device_id: deviceId,
    actor_user_id: 2,
    actor_name: 'María García',
    occurred_at: new Date().toISOString(),
    field,
    old_value: oldValue,
    new_value: newValue,
  });
}

const mockOperationalAlarms: any[] = [
  {
    alarmId: 'mock-alarm-1', companyId: 1, deviceId: 1, projectId: 1,
    alarmType: 'fall', severity: 'critical', status: 'active',
    detectedAt: new Date(Date.now() - 12 * 60 * 1000).toISOString(),
    telemetryTimestamp: new Date(Date.now() - 13 * 60 * 1000).toISOString(),
    acknowledgedBy: null, acknowledgedAt: null, metadata: { state: 16 },
    device: { id: 1, serialNumber: 'JIM-001', userName: 'Juan Martínez' },
    project: { id: 1, name: 'Proyecto Alfa' },
  },
  {
    alarmId: 'mock-alarm-2', companyId: 1, deviceId: 2, projectId: 1,
    alarmType: 'unhooked', severity: 'high', status: 'active',
    detectedAt: new Date(Date.now() - 48 * 60 * 1000).toISOString(),
    telemetryTimestamp: new Date(Date.now() - 49 * 60 * 1000).toISOString(),
    acknowledgedBy: null, acknowledgedAt: null, metadata: { state: 2 },
    device: { id: 2, serialNumber: 'JIM-002', userName: 'Pedro Fernandez' },
    project: { id: 1, name: 'Proyecto Alfa' },
  },
  {
    alarmId: 'mock-alarm-3', companyId: 1, deviceId: 1, projectId: 1,
    alarmType: 'generic', severity: 'medium', status: 'acknowledged',
    detectedAt: new Date(Date.now() - 2 * 24 * 60 * 60 * 1000).toISOString(),
    telemetryTimestamp: new Date(Date.now() - 2 * 24 * 60 * 60 * 1000).toISOString(),
    acknowledgedBy: 2, acknowledgedAt: new Date(Date.now() - 46 * 60 * 60 * 1000).toISOString(),
    metadata: {}, device: { id: 1, serialNumber: 'JIM-001', userName: 'Juan Martínez' },
    project: { id: 1, name: 'Proyecto Alfa' },
  },
];

// ── Route matching ───────────────────────────────────────────────────────────

export const mockInterceptor: HttpInterceptorFn = (req, next) => {
  if (!req.url.startsWith(API_BASE)) {
    return next(req);  // let assets / i18n pass through
  }

  const p   = path(req.url);
  const m   = req.method.toUpperCase();
  const body: any = req.body;

  // ── Auth ──────────────────────────────────────────────────────────────────
  // POST /auth/login
  if (p === '/auth/login' && m === 'POST') {
    const cred = SEED_CREDENTIALS.find(
      c => c.email.toLowerCase() === (body?.email ?? '').toLowerCase()
    ) ?? SEED_CREDENTIALS[0];   // default: superadmin

    const token = makeMockJWT({
      user: {
        id:         cred.userId,
        email:      cred.email,
        role_id:    cred.roleId,
        company_id: cred.companyId,
      },
      iat: 1714000000,
      exp: 9999999999,
    });
    return ok({ token });
  }

  // ── Companies ─────────────────────────────────────────────────────────────
  // GET /companies
  if (p === '/companies' && m === 'GET') {
    return ok(wrap(mockDb.companies));
  }
  // POST /companies
  if (p === '/companies' && m === 'POST') {
    const item = mockDb.add(mockDb.companies, body);
    return ok(mutationOk('Company created', item));
  }
  // GET /companies/:id
  let match = p.match(/^\/companies\/(\d+)$/);
  if (match) {
    const id = num(match[1]);
    if (m === 'GET') {
      const item = mockDb.findById(mockDb.companies, id);
      return item ? ok(wrap(item)) : notFound();
    }
    if (m === 'PUT') {
      const updated = mockDb.update(mockDb.companies, id, body);
      return updated ? ok(mutationOk('Company updated', updated)) : notFound();
    }
    if (m === 'DELETE') {
      const ok2 = mockDb.remove(mockDb.companies, id);
      return ok2 ? ok(mutationOk('Company deleted')) : notFound();
    }
  }

  // ── Users ─────────────────────────────────────────────────────────────────
  // GET /companies/:cid/users
  match = p.match(/^\/companies\/(\d+)\/users$/);
  if (match) {
    const cid = num(match[1]);
    if (m === 'GET') {
      return ok(wrap(mockDb.users.filter(u => u.company_id === cid)));
    }
    if (m === 'POST') {
      const item = mockDb.add(mockDb.users, { ...body, company_id: cid });
      return ok(mutationOk('User created', item));
    }
  }
  // GET/PUT/DELETE /companies/:cid/users/:id
  match = p.match(/^\/companies\/(\d+)\/users\/(\d+)$/);
  if (match) {
    const id = num(match[2]);
    if (m === 'GET') {
      const item = mockDb.findById(mockDb.users, id);
      return item ? ok(wrap(item)) : notFound();
    }
    if (m === 'PUT') {
      const updated = mockDb.update(mockDb.users, id, body);
      return updated ? ok(mutationOk('User updated', updated)) : notFound();
    }
    if (m === 'DELETE') {
      return mockDb.remove(mockDb.users, id)
        ? ok(mutationOk('User deleted')) : notFound();
    }
  }

  // ── Workers ───────────────────────────────────────────────────────────────
  match = p.match(/^\/companies\/(\d+)\/workers\/import$/);
  if (match && m === 'POST') {
    const rows: any[] = Array.isArray(body) ? body : [];
    const cid = num(match[1]);
    rows.forEach(r => mockDb.add(mockDb.workers, { ...r, company_id: cid }));
    return ok(mutationOk(`${rows.length} workers imported`));
  }

  match = p.match(/^\/companies\/(\d+)\/workers$/);
  if (match) {
    const cid = num(match[1]);
    if (m === 'GET') {
      const workers = mockDb.workers.filter(w => w.company_id === cid);
      const enriched = workers.map(w => {
        const dev = (mockDb.devices as any[]).find(d => d.user_id === w.id);
        return {
          ...w,
          device_id:             dev?.id             ?? null,
          device_serial_number:  dev?.serial_number  ?? null,
          device_company_id:     dev?.company_id     ?? null,
        };
      });
      return ok(wrap(enriched));
    }
    if (m === 'POST') {
      const item = mockDb.add(mockDb.workers, { ...body, company_id: cid });
      return ok(mutationOk('Worker created', item));
    }
  }
  match = p.match(/^\/companies\/(\d+)\/workers\/(\d+)$/);
  if (match) {
    const id = num(match[2]);
    if (m === 'GET') {
      const item = mockDb.findById(mockDb.workers, id);
      return item ? ok(wrap(item)) : notFound();
    }
    if (m === 'PUT') {
      const updated = mockDb.update(mockDb.workers, id, body);
      return updated ? ok(mutationOk('Worker updated', updated)) : notFound();
    }
    if (m === 'DELETE') {
      return mockDb.remove(mockDb.workers, id)
        ? ok(mutationOk('Worker deleted')) : notFound();
    }
  }

  // ── Zones ─────────────────────────────────────────────────────────────────
  match = p.match(/^\/companies\/(\d+)\/zones$/);
  if (match) {
    const cid = num(match[1]);
    if (m === 'GET') {
      return ok(wrap(mockDb.zones.filter(z => z.company_id === cid)));
    }
    if (m === 'POST') {
      const item = mockDb.add(mockDb.zones, { ...body, company_id: cid });
      return ok(mutationOk('Zone created', item));
    }
  }
  match = p.match(/^\/companies\/(\d+)\/zones\/(\d+)$/);
  if (match) {
    const id = num(match[2]);
    if (m === 'GET') {
      const item = mockDb.findById(mockDb.zones, id);
      return item ? ok(wrap(item)) : notFound();
    }
    if (m === 'PUT') {
      const updated = mockDb.update(mockDb.zones, id, body);
      return updated ? ok(mutationOk('Zone updated', updated)) : notFound();
    }
    if (m === 'DELETE') {
      return mockDb.remove(mockDb.zones, id)
        ? ok(mutationOk('Zone deleted')) : notFound();
    }
  }

  // ── Projects ──────────────────────────────────────────────────────────────
  match = p.match(/^\/companies\/(\d+)\/projects$/);
  if (match) {
    const cid = num(match[1]);
    if (m === 'GET') {
      return ok(wrap(mockDb.projects.filter(pr => pr.company_id === cid)));
    }
    if (m === 'POST') {
      const item = mockDb.add(mockDb.projects, { ...body, company_id: cid });
      return ok(mutationOk('Project created', item));
    }
  }
  match = p.match(/^\/companies\/(\d+)\/projects\/(\d+)$/);
  if (match) {
    const id = num(match[2]);
    if (m === 'GET') {
      const item = mockDb.findById(mockDb.projects, id);
      return item ? ok(wrap(item)) : notFound();
    }
    if (m === 'PUT') {
      const updated = mockDb.update(mockDb.projects, id, body);
      return updated ? ok(mutationOk('Project updated', updated)) : notFound();
    }
    if (m === 'DELETE') {
      return mockDb.remove(mockDb.projects, id)
        ? ok(mutationOk('Project deleted')) : notFound();
    }
  }

  // ── Devices ───────────────────────────────────────────────────────────────
  // GET /devices/:sn  (QR scanner lookup)
  match = p.match(/^\/devices\/([^/]+)$/);
  if (match && m === 'GET') {
    const sn = match[1];
    const item = mockDb.devices.find(d => d.serial_number === sn);
    return item ? ok(wrap(item)) : notFound();
  }

  match = p.match(/^\/companies\/(\d+)\/devices$/);
  if (match) {
    const cid = num(match[1]);
    if (m === 'GET') {
      return ok(wrap(cid === 0 ? mockDb.devices : mockDb.devices.filter(d => d.company_id === cid)));
    }
    if (m === 'POST') {
      const item = mockDb.add(mockDb.devices, { ...body, company_id: cid });
      recordDeviceActivity(item.id, 'device_created', null, item.serial_number);
      return ok(mutationOk('Device created', item));
    }
  }
  match = p.match(/^\/companies\/(\d+)\/devices\/(\d+)$/);
  if (match) {
    const cid = num(match[1]);
    const id  = num(match[2]);
    if (m === 'GET') {
      const item = mockDb.findById(mockDb.devices, id);
      return item ? ok(wrap(item)) : notFound();
    }
    if (m === 'PUT') {
      const existing: any = mockDb.findById(mockDb.devices, id);
      if (!existing) return notFound();
      const changes: Array<[string, string | null, string | null]> = [
        ['serial_number', existing.serial_number ?? null, body.serial_number ?? null],
        ['project', assignmentName('project', existing.project_id), assignmentName('project', body.project_id)],
        ['zone', assignmentName('zone', existing.zone_id), assignmentName('zone', body.zone_id)],
        ['primary_worker', assignmentName('primary_worker', existing.user_id), assignmentName('primary_worker', body.user_id)],
      ];
      const updated = mockDb.update(mockDb.devices, id, {
        ...body,
        user_name: assignmentName('primary_worker', body.user_id),
      });
      changes.forEach(([field, oldValue, newValue]) => recordDeviceActivity(id, field, oldValue, newValue));
      return updated ? ok(mutationOk('Device updated', updated)) : notFound();
    }
    if (m === 'DELETE') {
      return mockDb.remove(mockDb.devices, id)
        ? ok(mutationOk('Device deleted')) : notFound();
    }
  }

  match = p.match(/^\/companies\/(\d+)\/devices\/(\d+)\/activity$/);
  if (match && m === 'GET') {
    const id = num(match[2]);
    const activity = mockDb.deviceActivities
      .filter(item => item.device_id === id)
      .sort((a, b) => Date.parse(b.occurred_at) - Date.parse(a.occurred_at) || b.id - a.id);
    return ok(wrap(activity));
  }

  // ── Device configuration ─────────────────────────────────────────────────
  match = p.match(/^\/companies\/(\d+)\/devices\/(\d+)\/config$/);
  if (match) {
    const cid = num(match[1]);
    const did = num(match[2]);
    if (m === 'GET') {
      const cfg = mockDb.findConfig(cid, did);
      return cfg ? ok(wrap(cfg)) : ok(wrap({
        device_id: did, company_id: cid,
        desired_active_sampling_time: 30,
        desired_active_sending_time: 300,
        desired_state: 'active',
        update_name: null,
      }));
    }
    if (m === 'PUT') {
      const previous = mockDb.findConfig(cid, did);
      const cfg = mockDb.upsertConfig(cid, did, body);
      for (const field of [
        'desired_state', 'desired_active_sampling_time', 'desired_active_sending_time', 'update_name',
      ]) {
        const oldValue = previous?.[field] == null ? null : String(previous[field]);
        const newValue = body?.[field] == null ? null : String(body[field]);
        recordDeviceActivity(did, field, oldValue, newValue);
      }
      return ok(mutationOk('Config updated', cfg));
    }
  }

  // ── Device/project notification recipients ────────────────────────────────
  match = p.match(/^\/companies\/(\d+)\/(devices|projects)\/(\d+)\/notification-emails(?:\/(\d+))?$/);
  if (match) {
    const companyId = num(match[1]);
    const parentKey = match[2] === 'devices' ? 'device_id' : 'project_id';
    const parentId = num(match[3]);
    const notificationId = match[4] ? num(match[4]) : null;
    const belongsToParent = (item: any) => item.company_id === companyId && item[parentKey] === parentId;

    if (m === 'GET' && notificationId === null) {
      return ok(wrap(mockDb.notificationEmails.filter(belongsToParent)));
    }
    if (m === 'POST' && notificationId === null) {
      const item = mockDb.add(mockDb.notificationEmails, { ...body, company_id: companyId, [parentKey]: parentId });
      return ok(mutationOk('Notification email added', item));
    }
    if (m === 'DELETE' && notificationId !== null) {
      const item = mockDb.notificationEmails.find(email => email.id === notificationId && belongsToParent(email));
      return item && mockDb.remove(mockDb.notificationEmails, notificationId)
        ? ok(mutationOk('Notification email deleted'))
        : notFound();
    }
  }

  match = p.match(/^\/companies\/(\d+)\/(devices|projects)\/(\d+)\/notification-contacts(?:\/(\d+))?$/);
  if (match) {
    const companyId = num(match[1]);
    const parentKey = match[2] === 'devices' ? 'device_id' : 'project_id';
    const parentId = num(match[3]);
    const notificationId = match[4] ? num(match[4]) : null;
    const belongsToParent = (item: any) => item.company_id === companyId && item[parentKey] === parentId;

    if (m === 'GET' && notificationId === null) {
      return ok(wrap(mockDb.notificationContacts.filter(belongsToParent)));
    }
    if (m === 'POST' && notificationId === null) {
      const user = mockDb.findById(mockDb.users, body.user_id);
      if (!user) return notFound();
      const item = mockDb.add(mockDb.notificationContacts, {
        user_id: user.id,
        first_name: user.first_name,
        last_name: user.last_name,
        company_id: companyId,
        [parentKey]: parentId,
      });
      return ok(mutationOk('Notification contact added', item));
    }
    if (m === 'DELETE' && notificationId !== null) {
      const item = mockDb.notificationContacts.find(contact => contact.id === notificationId && belongsToParent(contact));
      return item && mockDb.remove(mockDb.notificationContacts, notificationId)
        ? ok(mutationOk('Notification contact deleted'))
        : notFound();
    }
  }

  // ── Alarm configuration ───────────────────────────────────────────────────
  match = p.match(/^\/alarmConfig\/([A-Z]+)\/(\d+)$/);
  if (match) {
    const parentType = match[1];
    const parentId   = num(match[2]);
    if (m === 'GET') {
      const cfg = mockDb.findAlarmConfig(parentType, parentId);
      return ok(cfg ? wrap(cfg) : wrap(null));
    }
    if (m === 'PUT') {
      const cfg = mockDb.upsertAlarmConfig({ ...body, parent_type: parentType, parent_id: parentId });
      return ok(mutationOk('Alarm config updated', cfg));
    }
    if (m === 'DELETE') {
      mockDb.removeAlarmConfig(parentType, parentId);
      return ok(mutationOk('Alarm config deleted'));
    }
  }

  // ── Operational dashboard ─────────────────────────────────────────────────
  match = p.match(/^\/companies\/(\d+)\/devices\/latest-telemetry$/);
  if (match && m === 'GET') {
    const deviceIds = (req.params.get('deviceIds') ?? '').split(',').map(Number).filter(Boolean);
    const now = new Date().toISOString();
    return ok(wrap({
      companyId: num(match[1]),
      requestedDeviceIds: deviceIds,
      returnedDeviceIds: deviceIds,
      devices: deviceIds.map((deviceId, index) => ({
        deviceId,
        measures: {
          device_state: { time: now, value: index % 4 === 0 ? 1 : 2, valueType: 'number', encoding: 'bitmask', bitmaskSchema: 'device_state' },
          state: { time: now, value: index % 3 === 0 ? 1 : 4, valueType: 'number', encoding: 'bitmask', bitmaskSchema: 'state' },
          temperature: { time: now, value: 20 + index, valueType: 'number', encoding: null, bitmaskSchema: null },
          battery: { time: now, value: Math.max(15, 95 - index * 5), valueType: 'number', encoding: null, bitmaskSchema: null },
        },
      })),
    }));
  }

  match = p.match(/^\/companies\/(\d+)\/devices\/(\d+)\/telemetry$/);
  if (match && m === 'GET') {
    const to = req.params.get('to') ?? new Date().toISOString();
    const from = req.params.get('from') ?? new Date(Date.now() - 24 * 60 * 60 * 1000).toISOString();
    const points = Array.from({ length: 12 }, (_, index) => ({
      time: new Date(Date.parse(from) + ((Date.parse(to) - Date.parse(from)) * index / 11)).toISOString(),
      value: 19 + Math.sin(index / 2) * 2,
    }));
    return ok(wrap({
      companyId: num(match[1]), deviceId: num(match[2]), from, to,
      mode: req.params.get('mode') ?? 'raw', bucket: null, aggregation: null,
      series: [
        { measure: 'temperature', valueType: 'number', encoding: null, bitmaskSchema: null, points },
        { measure: 'battery', valueType: 'number', encoding: null, bitmaskSchema: null, points: points.map((point, index) => ({ ...point, value: 90 - index })) },
        { measure: 'humidity', valueType: 'number', encoding: null, bitmaskSchema: null, points: points.map((point, index) => ({ ...point, value: 45.125 + index / 10 })) },
        { measure: 'device_state', valueType: 'number', encoding: 'bitmask', bitmaskSchema: 'device_state', points: points.map(point => ({ ...point, value: 2 })) },
        { measure: 'state', valueType: 'number', encoding: 'bitmask', bitmaskSchema: 'state', points: points.map(point => ({ ...point, value: 1 })) },
      ],
    }));
  }

  match = p.match(/^\/companies\/(\d+)\/alarms$/);
  if (match && m === 'GET') {
    const companyId = num(match[1]);
    const status = req.params.get('status');
    const projectId = Number(req.params.get('projectId'));
    const deviceId = Number(req.params.get('deviceId'));
    const from = req.params.get('from');
    const to = req.params.get('to');
    const alarms = mockOperationalAlarms.filter(alarm =>
      alarm.companyId === companyId
      && (!status || alarm.status === status)
      && (!projectId || alarm.projectId === projectId)
      && (!deviceId || alarm.deviceId === deviceId)
      && (!from || Date.parse(alarm.detectedAt) >= Date.parse(from))
      && (!to || Date.parse(alarm.detectedAt) < Date.parse(to))
    );
    return ok(wrap({ companyId, alarms, nextCursor: null }));
  }

  match = p.match(/^\/companies\/(\d+)\/alarms\/([^/]+)\/acknowledge$/);
  if (match && m === 'POST') {
    const alarm = mockOperationalAlarms.find(item => item.companyId === num(match[1]) && item.alarmId === decodeURIComponent(match[2]));
    if (!alarm) return notFound();
    alarm.status = 'acknowledged';
    alarm.acknowledgedBy = 2;
    alarm.acknowledgedAt = new Date().toISOString();
    return ok(wrap(alarm));
  }

  // ── Fallback: pass request through (shouldn't reach real API in mock mode) ─
  console.warn('[MockInterceptor] Unhandled route:', m, p);
  return next(req);
};
